import parse, { Element } from 'html-react-parser';

import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import Head from 'next/head';
import Link from 'next/link';

import { gql } from '@apollo/client';
import { FRESHDESK_FORMS, WEBSITE_NAME } from '../../lib/constants';
import IframeWrapper from '../../components/iframe-wrapper';
import Layout from '../../components/layout';
import Title from '../../components/title';

import {
  getLinkDomain,
  getTextFromDomNode,
  getYoastHeadSEO,
  isEmbeddedVideo,
  isImage,
  isImageLink,
  isLinkToArticle,
  isMatchingLink,
  isOptimisationCandidate,
  isParagraphImage,
  isParagraphWrappingIframe
} from '../../lib/utils';
import { client, getPageByURI } from 'apollo-client';
import { H1 } from '@components/atoms/typography';

const Affiliate = ({ page }) => {
  const router = useRouter();
  const pathname = router.pathname;
  const yoastHead = getYoastHeadSEO(page?.seo?.fullHead, pathname);
  const postContent = page?.content?.toString();

  return (
    <>
      <Head>
        {yoastHead ? (
          yoastHead
        ) : (
          <title>
            {'Become a Hub affiliate | '}
            {WEBSITE_NAME}
          </title>
        )}
      </Head>
      <Title>{'Become a Hub affiliate'}</Title>
      <Layout>
        <div className="article">
          <div className="relative px-4 pt-8 pb-20 bg-white sm:px-6 lg:pb-10 lg:px-8">
            <div className="relative mx-auto max-w-7xl pb-14">
              <article className="article">
                <div className="text-center">
                  <H1 className="text-center">{parse(page?.title)}</H1>
                </div>

                <div className="mt-3 text-base content-body text-black-400">
                  {parse(postContent, {
                    replace: (domNode: Element) => {
                      if (
                        domNode instanceof Element &&
                        domNode.attribs &&
                        domNode.attribs.class &&
                        domNode.attribs.class === 'epyt-video-wrapper'
                      ) {
                        // fixes double wrapping of iframe
                        domNode.attribs.class = '';

                        return { domNode };
                      }

                      if (isParagraphWrappingIframe(domNode)) {
                        const child = domNode.children[0] as Element;

                        return (
                          <IframeWrapper
                            id={child.attribs.id}
                            name={child.attribs.name}
                            src={child.attribs.src}
                            title={child.attribs.title}
                          />
                        );
                      }

                      if (isEmbeddedVideo(domNode)) {
                        return (
                          <IframeWrapper
                            id={domNode.attribs.id}
                            name={domNode.attribs.name}
                            src={domNode.attribs.src}
                            title={domNode.attribs.title}
                          />
                        );
                      }

                      if (isImage(domNode)) {
                        const height = domNode.attribs.height;
                        const width = domNode.attribs.width;
                        return (
                          <>
                            {height && isOptimisationCandidate(domNode) ? (
                              <Image
                                className="article-image"
                                src={domNode.attribs.src}
                                height={Number(height)}
                                width={Number(width)}
                                alt={domNode.attribs.alt}
                                layout="responsive"
                                // WP Engine 403s Vercel's optimiser on cache-miss (FFH-1688)
                                unoptimized
                              />
                            ) : (
                              <img
                                alt={domNode.attribs.alt}
                                src={domNode.attribs.src}
                                className="mx-auto"
                              />
                            )}
                          </>
                        );
                      }

                      if (isParagraphImage(domNode)) {
                        const img = domNode.children[0] as Element;
                        const height = img.attribs.height;
                        const width = img.attribs.width;
                        return (
                          <div>
                            {height && isOptimisationCandidate(img) ? (
                              <Image
                                className="article-image"
                                src={img.attribs.src}
                                height={Number(height)}
                                width={Number(width)}
                                alt={img.attribs.alt}
                                layout="responsive"
                                // WP Engine 403s Vercel's optimiser on cache-miss (FFH-1688)
                                unoptimized
                              />
                            ) : (
                              <img
                                alt={domNode.attribs.alt}
                                src={domNode.attribs.src}
                                className="mx-auto"
                              />
                            )}
                          </div>
                        );
                      }

                      if (isImageLink(domNode)) {
                        const img = domNode.children[0] as Element;
                        const height = img.attribs.height;
                        const width = img.attribs.width;
                        return (
                          <div className="mb-10">
                            <Link
                              href={domNode.attribs.href.replace(
                                process.env
                                  .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                                `${getLinkDomain(window.location.origin)}`
                              )}
                              aria-label={domNode.attribs.href.replace(
                                process.env
                                  .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                                `${getLinkDomain(window.location.origin)}`
                              )}
                            >
                              {height && isOptimisationCandidate(img) ? (
                                <Image
                                  className="article-image"
                                  src={img.attribs.src}
                                  height={Number(height)}
                                  width={Number(width)}
                                  alt={img.attribs.alt}
                                  layout={'responsive'}
                                  // WP Engine 403s Vercel's optimiser on cache-miss (FFH-1688)
                                  unoptimized
                                />
                              ) : (
                                <img
                                  alt={img.attribs.alt}
                                  src={img.attribs.src}
                                  className="mx-auto"
                                />
                              )}
                            </Link>
                          </div>
                        );
                      }

                      if (
                        domNode.attribs &&
                        domNode.attribs.class === 'ff_inline_message'
                      ) {
                        return <></>;
                      }

                      if (isMatchingLink(domNode, '/premium-membership')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/join`;

                        return { domNode };
                      }

                      if (isMatchingLink(domNode, '/?post_type=post&p=11173')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/premier-league-set-piece-takers`;

                        return { domNode };
                      }

                      if (isMatchingLink(domNode, '/membership-registration')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/join`;

                        return { domNode };
                      }

                      if (isMatchingLink(domNode, '/freetrial')) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/join`;

                        return { domNode };
                      }

                      if (
                        isMatchingLink(
                          domNode,
                          '/fanteam-fantasy-football-2021-22-tips-the-ultimate-guide'
                        )
                      ) {
                        domNode.attribs.href = `${getLinkDomain(
                          process.env.NEXT_PUBLIC_HOSTNAME
                        )}/fanteam-fantasy-football-tips-the-ultimate-guide`;

                        return { domNode };
                      }

                      if (isLinkToArticle(domNode)) {
                        return (
                          <Link
                            href={domNode.attribs.href.replace(
                              process.env
                                .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                              `${getLinkDomain(window.location.origin)}`
                            )}
                            aria-label={domNode.attribs.href.replace(
                              process.env
                                .NEXT_PUBLIC_MEMBERPRESS_BASE_URL as string,
                              `${getLinkDomain(window.location.origin)}`
                            )}
                          >
                            {getTextFromDomNode(domNode)}
                          </Link>
                        );
                      }
                    }
                  })}
                </div>
                <div>
                  <h3 className="mt-8">{'Affiliate sign up'}</h3>
                  <p className="mb-4">
                    {
                      'Ready to apply? Open our affiliate sign up form and a member of the team will be in touch.'
                    }
                  </p>
                  <a
                    href={FRESHDESK_FORMS.AFFILIATE_SIGN_UP}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex justify-center text-center rounded-lg transition-colors font-open-sans font-bold text-base border-2 border-transparent py-[7px] px-3 text-white bg-primary-600 hover:bg-primary-800 active:bg-neutral-800"
                  >
                    {'Apply to become an affiliate'}
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPageByURI('/become-an-affiliate')}
    `
  });

  const page = data.pageBy;

  return {
    props: { page }
  };
};

export default Affiliate;
